import { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../ui/Button';
import nurseryService from '../../services/nursery';
import { stockItemLabel } from '../../utils/nurseryRowSlots';
import type { LotStage, NurseryLocation, NurseryLot, NurseryStockItem } from '../../types/nursery';

const STAGES: LotStage[] = ['sown', 'seedling', 'transplanted', 'grafted', 'budded', 'growing', 'potted', 'ready'];

interface Props {
  location: NurseryLocation;
  /** "Row 12 left · position 30", or the location's own name. */
  title: string;
  /** Lots already standing here. */
  lotsHere: NurseryLot[];
  stockItems: NurseryStockItem[];
  onPlaced: (note: string) => void;
  onCancel: () => void;
}

type Tree = Pick<NurseryLot, 'cultivar_id' | 'rootstock_id' | 'tree_type_id'>;
const sameTree = (a: Tree, b: Tree) =>
  a.cultivar_id === b.cultivar_id && (a.rootstock_id ?? null) === (b.rootstock_id ?? null) && a.tree_type_id === b.tree_type_id;

/**
 * Put a stock item into one location, from the locations screen: the same
 * lot the full Place a Lot form makes, with the place already chosen.
 *
 * A row position holds one stock item. More lots of it can join as they come
 * ready, so a filled position offers only the tree already standing there.
 * A seed bed, a polyhouse or a despatch area holds anything.
 */
export default function PlaceLotPanel({ location, title, lotsHere, stockItems, onPlaced, onCancel }: Props) {
  const isPosition = location.row_number != null && location.sequence_number != null && !location.is_despatch_area;
  const standing = lotsHere.filter(l => l.status === 'active' && l.current_count > 0);
  const held = isPosition && standing.length ? standing[0] : null;
  const mixed = held != null && standing.some(l => !sameTree(l, held));
  const choices = held ? (mixed ? [] : stockItems.filter(i => sameTree(i, held))) : stockItems;

  const [stockItemId, setStockItemId] = useState<number | ''>(held && choices.length ? choices[0].id : '');
  const [count, setCount] = useState<number | ''>('');
  const [stage, setStage] = useState<LotStage>('sown');
  const [plantDate, setPlantDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const item = choices.find(i => i.id === stockItemId);
  const fullForm = `/nursery/lots/new?location_id=${location.id}`;

  const place = async () => {
    if (!item) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryService.createLot({
        cultivar_id: item.cultivar_id,
        rootstock_id: item.rootstock_id,
        tree_type_id: item.tree_type_id,
        location_id: location.id,
        current_stage: stage,
        initial_count: count === '' ? 0 : count,
        plant_date: plantDate || null,
        record_origin: true,
      });
      if (res.success) onPlaced(`Placed ${count === '' ? 0 : count} × ${stockItemLabel(item)} at ${title}.`);
      else setError((res as { error?: { message?: string } }).error?.message || 'Failed to place the lot');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to place the lot');
    } finally {
      setBusy(false);
    }
  };

  const field = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm bg-white';

  return (
    <div className="bg-primary-50/50 border border-primary-200 rounded-lg px-3 py-3 mt-2 text-sm">
      <p className="font-semibold text-neutral-800 break-words">Place stock at {title}</p>

      {lotsHere.length > 0 && (
        <ul className="mt-1 text-neutral-600">
          {lotsHere.map(l => (
            <li key={l.id} className="break-words">
              Here now:{' '}
              <Link to={`/nursery/lots/${l.id}`} className="text-primary-700 hover:underline">
                {stockItemLabel(l)}
              </Link>
              {' '}· {l.current_count} {l.current_stage}
            </li>
          ))}
        </ul>
      )}

      {held && !mixed && choices.length > 0 && (
        <p className="mt-2 text-neutral-600 break-words">
          A position holds one stock item. More {stockItemLabel(held)} can go here; anything else needs another position.
        </p>
      )}

      {mixed ? (
        <p className="mt-2 text-amber-800 break-words">
          This position holds more than one stock item. A position holds one: move the others to a position of their own
          before placing more here.
        </p>
      ) : held && choices.length === 0 ? (
        <p className="mt-2 text-amber-800 break-words">
          The trees here are not one of your stock items, so nothing more can be placed with them. Choose another position.
        </p>
      ) : stockItems.length === 0 ? (
        <p className="mt-2 text-neutral-600 break-words">
          No stock items yet. Only stock items can be placed:{' '}
          <Link to="/nursery/stock-items/new" className="text-primary-700 hover:underline">add a stock item</Link>.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 mt-3 sm:grid-cols-4">
          <div className="col-span-2 sm:col-span-4">
            <label htmlFor="place-stock-item" className="block font-medium text-neutral-700 mb-1">Stock item</label>
            <select id="place-stock-item" value={stockItemId} className={field}
              onChange={e => setStockItemId(e.target.value ? Number(e.target.value) : '')}>
              {!held && <option value="">Choose a stock item…</option>}
              {choices.map(i => <option key={i.id} value={i.id}>{stockItemLabel(i)}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="place-count" className="block font-medium text-neutral-700 mb-1">Count</label>
            <input id="place-count" type="number" min="0" value={count} className={field}
              onChange={e => setCount(e.target.value === '' ? '' : parseInt(e.target.value, 10))} />
          </div>
          <div>
            <label htmlFor="place-stage" className="block font-medium text-neutral-700 mb-1">Stage</label>
            <select id="place-stage" value={stage} onChange={e => setStage(e.target.value as LotStage)} className={field}>
              {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label htmlFor="place-date" className="block font-medium text-neutral-700 mb-1">Plant / sow date</label>
            <input id="place-date" type="date" value={plantDate} onChange={e => setPlantDate(e.target.value)} className={field} />
          </div>
        </div>
      )}

      {error && <p className="mt-2 text-red-700 break-words">{error}</p>}

      <div className="flex items-center gap-2 flex-wrap mt-3">
        {choices.length > 0 && (
          <Button variant="primary" size="sm" onClick={place} disabled={busy || !item}>
            {busy ? 'Placing…' : 'Place lot'}
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={onCancel} disabled={busy}>Cancel</Button>
        {choices.length > 0 && (
          <Link to={fullForm} className="text-primary-700 hover:underline">More options</Link>
        )}
      </div>
    </div>
  );
}
